
import { useEffect, useState, useCallback, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useAuth } from "@/context/AuthContext";
import { useSidebar } from "@/hooks/use-sidebar";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw } from "lucide-react";

// Keys for localStorage with TTL
const CACHE_KEYS = {
  EMPLOYEES: 'mfo_cache_employees',
  DEPARTMENTS: 'mfo_cache_departments',
  MFO_ITEMS: 'mfo_cache_mfo_items',
  MFO_CODES: 'mfo_cache_mfo_codes',
  NOTIFICATIONS: 'mfo_cache_notifications',
  KANBAN: 'mfo_cache_kanban',
  LAST_UPDATED: 'mfo_cache_last_updated'
};

// Reduced cache TTL to 2 hours (in milliseconds)
const CACHE_TTL = 2 * 60 * 60 * 1000;

// Maximum loading time before direct fetch (in milliseconds)
const MAX_LOADING_TIME = 5000; // Increased to 5 seconds

const Layout = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { isOpen } = useSidebar();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [fetchingInProgress, setFetchingInProgress] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [fetchErrorOccurred, setFetchErrorOccurred] = useState(false);
  const fetchAttempts = useRef(0);

  useEffect(() => {
    // If user is not logged in and finished loading, redirect to login
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Set a timeout to trigger direct fetch if loading takes too long
  useEffect(() => {
    if (!user || dataLoaded || fetchingInProgress) return;

    const timeout = setTimeout(() => {
      fetchAllDataDirectly();
    }, MAX_LOADING_TIME);

    setLoadingTimeout(timeout);

    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [user, dataLoaded, fetchingInProgress]);

  const handleManualRefresh = useCallback(() => {
    setDataLoaded(false);
    setFetchingInProgress(false);
    setFetchErrorOccurred(false);
    fetchAttempts.current = 0;
    
    // Clear cache
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    toast({
      title: "Refreshing Data",
      description: "Manually refreshing all application data...",
    });
    
    fetchAllDataDirectly();
  }, [toast]);

  // Attempt to load data from localStorage first
  useEffect(() => {
    if (!user) {
      return;
    }
    
    try {
      const lastUpdated = localStorage.getItem(CACHE_KEYS.LAST_UPDATED);
      
      if (lastUpdated) {
        const cacheAge = Date.now() - parseInt(lastUpdated);
        const cacheIsValid = cacheAge < CACHE_TTL; // 2 hours cache validity
        
        if (cacheIsValid) {
          
          // Get all cached data
          const employeesData = JSON.parse(localStorage.getItem(CACHE_KEYS.EMPLOYEES) || '[]');
          const departmentsData = JSON.parse(localStorage.getItem(CACHE_KEYS.DEPARTMENTS) || '[]');
          const mfoItemsData = JSON.parse(localStorage.getItem(CACHE_KEYS.MFO_ITEMS) || '[]');
          const mfoCodesData = JSON.parse(localStorage.getItem(CACHE_KEYS.MFO_CODES) || '[]');
          const notificationsData = JSON.parse(localStorage.getItem(CACHE_KEYS.NOTIFICATIONS) || '[]');
          const kanbanData = JSON.parse(localStorage.getItem(CACHE_KEYS.KANBAN) || '{"columns":[],"cards":[]}');
          
          // Set data in React Query cache
          queryClient.setQueryData(['employees'], employeesData);
          queryClient.setQueryData(['departments'], departmentsData);
          queryClient.setQueryData(['mfoItems', null, user.id], mfoItemsData);
          queryClient.setQueryData(['mfoCodes'], mfoCodesData);
          queryClient.setQueryData(['notifications', 'list', { userId: user.id }], notificationsData);
          queryClient.setQueryData(['kanban'], kanbanData);
          
          setDataLoaded(true);
          
          // Check if cache is older than 30 minutes (for refresh in background)
          const shouldRefreshInBackground = cacheAge > (30 * 60 * 1000);
          
          if (shouldRefreshInBackground) {
            setTimeout(() => {
              fetchAllDataDirectly(true);
            }, 2000); // Delay background refresh by 2 seconds
          }
          
          return;
        } else {
          console.log("Cache expired, fetching fresh data");
        }
      } else {
        console.log("No cache found, fetching fresh data");
      }
      
      // If we reach here, we should directly fetch fresh data
      fetchAllDataDirectly();
      
    } catch (error) {
      // If there's an error with the cache, fetch fresh data
      fetchAllDataDirectly();
    }
  }, [user, queryClient]);

  // Direct Supabase fetching without using React Query
  const fetchAllDataDirectly = useCallback(async (isBackgroundFetch = false) => {
    if (!user) return;
    if (!isBackgroundFetch && dataLoaded) return;
    if (fetchingInProgress) return;
    
    setFetchingInProgress(true);
    fetchAttempts.current += 1;
  
    
    // If this is a retry after multiple failures, add a short delay
    if (fetchAttempts.current > 1 && !isBackgroundFetch) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    try {
      setFetchErrorOccurred(false);
      
      // 1. Fetch employees

      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select(`
          *,
          departments(id, name),
          employee_skills(id, skill)
        `);
        
      if (employeesError) {
        console.error("Error fetching employees:", employeesError);
        throw employeesError;
      }
    
      
      // 2. Fetch departments
    
      const { data: departmentsData, error: departmentsError } = await supabase
        .from('departments')
        .select(`
          *,
          department_goals(id, goal)
        `);
        
      if (departmentsError) {
        console.error("Error fetching departments:", departmentsError);
        throw departmentsError;
      }
   
      
      // 3. Fetch MFO items

      const { data: mfoItemsData, error: mfoItemsError } = await supabase
        .from('mfo_items')
        .select(`
          id, code, title, budget, priority, due_date, status, 
          progress, q1, q2, q3, q4, department_id,
          departments (id, name),
          mfo_success_indicators (indicator),
          mfo_assignees (
            employee:employees (id, name, avatar)
          )
        `);
        
      if (mfoItemsError) {
        console.error("Error fetching MFO items:", mfoItemsError);
        throw mfoItemsError;
      }

      
      // 4. Fetch MFO codes
      const { data: mfoCodesData, error: mfoCodesError } = await supabase
        .from('mfo_codes')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (mfoCodesError) {
        console.error("Error fetching MFO codes:", mfoCodesError);
        throw mfoCodesError;
      }
      
      // 5. Fetch notifications
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (notificationsError) {
        console.error("Error fetching notifications:", notificationsError);
        throw notificationsError;
      }
      
      // 6. Fetch kanban data
      
      // 6a. Fetch columns
      const { data: columnsData, error: columnsError } = await supabase
        .from("kanban_columns")
        .select("*")
        .order("order_index");
        
      if (columnsError) {
        console.error("Error fetching kanban columns:", columnsError);
        throw columnsError;
      }
      
      // 6b. Fetch cards
      const { data: cardsData, error: cardsError } = await supabase
        .from("kanban_cards")
        .select(`
          *,
          assignees:kanban_card_assignees(
            employee_id, 
            employees(id, name, avatar)
          )
        `);
        
      if (cardsError) {
        console.error("Error fetching kanban cards:", cardsError);
        throw cardsError;
      }
      
      const kanbanData = {
        columns: columnsData || [],
        cards: cardsData || []
      };
      
      
      // Store everything in React Query cache
      queryClient.setQueryData(['employees'], employeesData || []);
      queryClient.setQueryData(['departments'], departmentsData || []);
      queryClient.setQueryData(['mfoItems', null, user.id], mfoItemsData || []);
      queryClient.setQueryData(['mfoCodes'], mfoCodesData || []);
      queryClient.setQueryData(['notifications', 'list', { userId: user.id }], notificationsData || []);
      queryClient.setQueryData(['kanban'], kanbanData);
      
      // Also cache to localStorage with timestamp for TTL check
      localStorage.setItem(CACHE_KEYS.EMPLOYEES, JSON.stringify(employeesData || []));
      localStorage.setItem(CACHE_KEYS.DEPARTMENTS, JSON.stringify(departmentsData || []));
      localStorage.setItem(CACHE_KEYS.MFO_ITEMS, JSON.stringify(mfoItemsData || []));
      localStorage.setItem(CACHE_KEYS.MFO_CODES, JSON.stringify(mfoCodesData || []));
      localStorage.setItem(CACHE_KEYS.NOTIFICATIONS, JSON.stringify(notificationsData || []));
      localStorage.setItem(CACHE_KEYS.KANBAN, JSON.stringify(kanbanData));
      localStorage.setItem(CACHE_KEYS.LAST_UPDATED, Date.now().toString());
      
      if (!isBackgroundFetch) {
        setDataLoaded(true);
        
        // Clear any pending loading timeout
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          setLoadingTimeout(null);
        }
      }
      
      // Reset fetch attempts counter
      fetchAttempts.current = 0;
    } catch (error) {
      console.error("Error during direct data fetch:", error);
      setFetchErrorOccurred(true);
      
      // Try to use cache as fallback
      try {
        if (localStorage.getItem(CACHE_KEYS.LAST_UPDATED)) {
          
          const employeesData = JSON.parse(localStorage.getItem(CACHE_KEYS.EMPLOYEES) || '[]');
          const departmentsData = JSON.parse(localStorage.getItem(CACHE_KEYS.DEPARTMENTS) || '[]');
          const mfoItemsData = JSON.parse(localStorage.getItem(CACHE_KEYS.MFO_ITEMS) || '[]');
          const mfoCodesData = JSON.parse(localStorage.getItem(CACHE_KEYS.MFO_CODES) || '[]');
          const notificationsData = JSON.parse(localStorage.getItem(CACHE_KEYS.NOTIFICATIONS) || '[]');
          const kanbanData = JSON.parse(localStorage.getItem(CACHE_KEYS.KANBAN) || '{"columns":[],"cards":[]}');
          
          queryClient.setQueryData(['employees'], employeesData);
          queryClient.setQueryData(['departments'], departmentsData);
          queryClient.setQueryData(['mfoItems', null, user.id], mfoItemsData);
          queryClient.setQueryData(['mfoCodes'], mfoCodesData);
          queryClient.setQueryData(['notifications', 'list', { userId: user.id }], notificationsData);
          queryClient.setQueryData(['kanban'], kanbanData);
          
          if (!isBackgroundFetch) {
            setDataLoaded(true);
            
            // Clear any pending loading timeout
            if (loadingTimeout) {
              clearTimeout(loadingTimeout);
              setLoadingTimeout(null);
            }
          }
          
          // If not a background fetch and fetch attempts are low, try again after a delay
          if (!isBackgroundFetch && fetchAttempts.current < 3) {
            setTimeout(() => {
              setFetchingInProgress(false);
              fetchAllDataDirectly();
            }, 3000 * fetchAttempts.current); // Progressive backoff
          }
        } else {
          // If there's no cache and fetch failed, just set empty data to allow app to load

          queryClient.setQueryData(['employees'], []);
          queryClient.setQueryData(['departments'], []);
          queryClient.setQueryData(['mfoItems', null, user.id], []);
          queryClient.setQueryData(['mfoCodes'], []);
          queryClient.setQueryData(['notifications', 'list', { userId: user.id }], []);
          queryClient.setQueryData(['kanban'], { columns: [], cards: [] });
          
          if (!isBackgroundFetch) {
            setDataLoaded(true);
            
            // Clear any pending loading timeout
            if (loadingTimeout) {
              clearTimeout(loadingTimeout);
              setLoadingTimeout(null);
            }
            
            // Show error message with refresh button
            toast({
              title: "Data Loading Error",
              description: "Failed to load data. Please try refreshing.",
              variant: "destructive",
            });
          }
        }
      } catch (cacheError) {
        console.error("Error using cache as fallback:", cacheError);
        // Last resort - just set empty data to allow app to load
        if (!isBackgroundFetch) {
          setDataLoaded(true);
          
          // Clear any pending loading timeout
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            setLoadingTimeout(null);
          }
          
          // Show error message with refresh button
          toast({
            title: "Data Loading Error",
            description: "Failed to load data. Please try refreshing.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setFetchingInProgress(false);
    }
  }, [user, dataLoaded, loadingTimeout, queryClient, toast]);
  
  useEffect(() => {
    if (user && !authLoading && !dataLoaded && !fetchingInProgress) {
      fetchAllDataDirectly();
    }
  }, [user, authLoading, dataLoaded, fetchingInProgress, fetchAllDataDirectly]);

  // Show loading indicator while data is being loaded
  if (authLoading || (user && !dataLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-muted-foreground">
            {authLoading ? "Checking authentication..." : `Loading application data ${fetchingInProgress ? '(fetching...)' : ''}`}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {user ? `User: ${user.email}` : "No user found"}
          </p>
          
          {/* Show refresh button if there was an error and we've had multiple fetch attempts */}
          {fetchErrorOccurred && fetchAttempts.current >= 2 && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={handleManualRefresh}
              disabled={fetchingInProgress}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Data
            </Button>
          )}
          
          {fetchAttempts.current > 1 && (
            <div className="text-xs text-amber-500 mt-4 max-w-md text-center">
              Having trouble loading data? Try refreshing the page or checking your internet connection.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <div className={`${isOpen ? 'w-64' : 'w-0'} transition-all duration-300 flex-shrink-0`}>
          <Sidebar />
        </div>
        <main className="flex-1 overflow-auto bg-muted/30 px-4 py-24 relative">
          <div className="container mx-auto max-w-7xl">
            {fetchErrorOccurred && (
              <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between">
                <p className="text-sm text-amber-700">
                  Some data may not be up to date due to connection issues.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleManualRefresh}
                  disabled={fetchingInProgress}
                >
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Refresh Data
                </Button>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
